import React, { useEffect, useState, useCallback } from "react";
import { MapPin, Filter } from "react-feather";
import { Select } from "semantic-ui-react";

// API Services
import {
  getBulkDropdownData,
  getDropdownData,
  getOtherRefSearchDropdown,
} from "../../api/keyvalue";
import { getLocation, searchLocation } from "../../api/locationCreationApi";
import Grid from "../../components/Grid";
import LoadingSpinner from "../../components/LoadingSpinner";
import Button from "../../components/input/Button";
import { countryStartsWith } from "../../api/countryApi";
import { stateByCountryAndStartsWith } from "../../api/stateApi";
import { districtByStateAndStartsWith } from "../../api/districtApi";
import SearchDropdown from "../../components/input/SearchDropdown";
import SideDrawer from "../../components/input/SideDrawer";
import AddLocation from "../../components/location/AddLocation";
import SelectRenderer from "../../components/renderer/SelectRenderer";
import {
  ActiveCellStyle,
  ResizableScrollingTextRenderer,
  StatusCellStyle,
} from "../../components/renderer/StyleRenderer";
import { formatFromJsonDate } from "../../dateConfig";
import Input from "../../components/input/Input";
import { FORMS, MODULES } from "../../const";
import useFirstAndLastAuthorizedTab from "../../hooks/useFirstAndLastAuthorizedTab";
import { useSelector } from "react-redux";

const LocationCreation = () => {
  const userData = useSelector((state) => state.user.userDetails);
  const user = useSelector((state) => state.user);

  const { firstViableTab } = useFirstAndLastAuthorizedTab({
    module: MODULES.Master,
    section: FORMS.LOCATION,
  });

  const [tab, setTab] = useState(1);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState(1);
  const [searchClicked, setSearchClicked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchFieldReset, setSearchFieldReset] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [editLocation, setEditLocation] = useState({});
  const [searchSubmitLoading, setSearchSubmitLoading] = useState(false);
  const [isResultLoading, setIsResultLoading] = useState(false);

  // Variables
  const [locationType, setLocationType] = useState("");
  const [locationName, setLocationName] = useState("");
  const [createdFrom, setCreatedFrom] = useState(null);
  const [createdTo, setCreatedTo] = useState(null);
  const [customer, setCustomer] = useState("");
  const [statusType, setStatusType] = useState(2);
  const [activeType, setActiveType] = useState(1);
  const [locationCode, setLocationCode] = useState("");
  const [longitude, setLongitude] = useState("");
  const [latitude, setLatitude] = useState("");
  const [country, setCountry] = useState("");
  const [state, setState] = useState(null);
  const [district, setDistrict] = useState(null);
  const [searchRes, setSearchRes] = useState([]);

  // dropdown data
  const [locTypeOptions, setLocTypeOptions] = useState([]);
  const [statusOpt, setStatusOpt] = useState([]);
  const [activeOptions, setActiveOptions] = useState([]);

  const [currentErrors, setCurrentErrors] = useState({});
  const [isCountrySet, setIsCountrySet] = useState(false);
  const [isStateSet, setIsStateSet] = useState(false);

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      const {
        active,
        location_approval: status,
        loc_type: locTypeOpt,
      } = await getBulkDropdownData([
        { key: "active" },
        {
          key: "location_approval",
          authenticated: true,
          authorities: [
            "LOCATION_CUSTOMER_FORM_PENDING",
            "LOCATION_CUSTOMER_FORM_APPROVE",
            "LOCATION_CUSTOMER_FORM_REJECT",
            "LOCATION_CUSTOMER_FORM_CUSTOMER_APPROVE",
          ],
        },
        { key: "loc_type" },
      ]);

      setLocTypeOptions(locTypeOpt);
      setActiveOptions(active);
      setStatusOpt(status);

      setIsLoading(false);
    })();
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    setSearchSubmitLoading(true);
    setSearchClicked(true);

    const response = await searchLocation({
      fromDate: createdFrom,
      toDate: createdTo,
      locationType: locationType,
      locationName: locationName,
      customer: customer,
      status: statusType,
      active: activeType,
      locationCode: locationCode,
      locationLong: longitude,
      locationLatt: latitude,
      country: country,
      state: state,
      district: district,
    });

    const formattedResponse = response.map((loc) => ({
      ...loc,
      fromDate: loc.fromDate ? formatFromJsonDate(loc.fromDate) : "",
      toDate: loc.toDate ? formatFromJsonDate(loc.toDate) : "",
    }));

    setSearchRes(formattedResponse);
    setSearchSubmitLoading(false);
    setActiveAccordion(false);
  };

  const handleReset = (e) => {
    e.preventDefault();
    setLocationType("");
    setLocationName("");
    setCustomer("");
    setStatusType("");
    setActiveType("");
    setSearchFieldReset(true);
    setSearchRes([]);
    setCreatedFrom(null);
    setCreatedTo(null);
    setLocationCode("");
    setLongitude("");
    setLatitude("");
    setCountry("");
    setState("");
    setDistrict("");
    setResetKey((currentKey) => currentKey + 1);
  };

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
    setEditMode(false);
    setEditLocation(null);
    setTab(1);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleRowDoubleClick = async ({ data }) => {
    setIsResultLoading(true);

    try {
      const response = await getLocation(data.locaId);

      if (response) {
        setEditLocation(response);
        setEditMode(true);
        setDrawerOpen(true);
      }
    } catch (error) {
      console.error("Error fetching location details:", error);
    } finally {
      setIsResultLoading(false);
    }
  };

  const handleAccordionClick = (index) => {
    setActiveAccordion(index === activeAccordion ? null : index);
  };

  const customerCellRenderer = (params) => {
    if (params.value) {
      const customers = params.value.split(", ");

      const handleChange = (selectedValue) => {
        console.log("Customer selected:", selectedValue);
      };

      if (customers.length > 1) {
        return (
          <SelectRenderer
            onChangeHandler={handleChange}
            value={null}
            placeholder="Select Customer"
            options={customers.map((customer) => ({
              key: customer,
              text: customer,
              value: customer,
            }))}
          />
        );
      } else {
        return <ResizableScrollingTextRenderer value={params.value} />;
      }
    }
    return null;
  };

    return (
        <div className="location-creation-wrapper" style={{ padding: "10px" }}>
            {/* Top Header Navigation */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "15px",
                    borderBottom: "1px solid #e0e0e0",
                    paddingBottom: "10px",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <MapPin size={18} color="#2185d0" />
                    <h2 style={{ margin: 0, fontSize: "1.2rem", color: "#333" }}>
                        Location Master Management
                    </h2>
                </div>

                <Button
                    template="drawerOpen"
                    disabled={firstViableTab === -1}
                    onClick={handleDrawerOpen}
                    authenticated={true}
                    form={FORMS.LOCATION.LOC.form}
                    module={MODULES.Master}
                    editMode={false}
                />
            </div>

            {/* Collapsible Search Criteria Section */}
            <div
                style={{
                    backgroundColor: "#fff",
                    border: "1px solid #d4d4d5",
                    borderRadius: "6px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    marginBottom: "20px",
                    overflow: "hidden",
                }}
            >
                <div
                    onClick={() => setActiveAccordion(activeAccordion === 1 ? null : 1)}
                    style={{
                        padding: "12px 20px",
                        backgroundColor: "#f9fafb",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        cursor: "pointer",
                        borderBottom: activeAccordion === 1 ? "1px solid #d4d4d5" : "none",
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Filter size={16} color="#666" />
                        <span style={{ fontWeight: "600", color: "#4183c4" }}>
                            Search Filters
                        </span>
                    </div>
                    <span style={{ color: "#888", fontSize: "0.85rem" }}>
                        {activeAccordion === 1 ? "▲ Collapse" : "▼ Expand Filter Options"}
                    </span>
                </div>

                {activeAccordion === 1 && (
                    <form className="ui form" style={{ padding: "20px" }}>
                        {/* Primary Details Row */}
                        <div className="four fields">
                            <div className="field">
                                <label>Location Type</label>
                                <Select
                                    style={{ height: "20px" }}
                                    clearable
                                    options={locTypeOptions}
                                    placeholder="Select Type"
                                    value={locationType}
                                    onChange={(e, data) => setLocationType(data.value)}
                                />
                            </div>

                            <div className="field">
                                <label>Location Name</label>
                                <input
                                    type="text"
                                    placeholder="Search Location Name"
                                    value={locationName}
                                    onChange={(e) => setLocationName(e.target.value)}
                                />
                            </div>

                            <Input
                                label="Location Code"
                                placeholder="e.g. LOC000123"
                                error={currentErrors.locationCode}
                                resetError={() =>
                                    setCurrentErrors((prev) => {
                                        const { locationCode: _, ...rest } = prev;
                                        return rest;
                                    })
                                }
                                name="locationCode"
                                value={locationCode}
                                onChange={(e) => setLocationCode(e.target.value)}
                            />

                            <SearchDropdown
                                key={`cust-${resetKey}`}
                                editMode={false}
                                clearable
                                onClear={() => setCustomer("")}
                                onResultSelect={(data) => setCustomer(data)}
                                api={getOtherRefSearchDropdown}
                                apiDependency={
                                    userData?.type === 1
                                        ? { type: "stakeholder_type", value: "Customer" }
                                        : {
                                            type: "user_stakeholder",
                                            value: "Requesting Customer",
                                            param2: user?.username,
                                        }
                                }
                                label="Customer"
                            />
                        </div>

                        {/* Date Range & Geo Coordinates Row */}
                        <div className="four fields">
                            <div className="field">
                                <label>Created From</label>
                                <input
                                    type="date"
                                    value={createdFrom || ""}
                                    max={
                                        createdTo
                                            ? createdTo
                                            : new Date().toISOString().split("T")[0]
                                    }
                                    onChange={(e) => setCreatedFrom(e.target.value)}
                                />
                            </div>

                            <div className="field">
                                <label>Created To</label>
                                <input
                                    type="date"
                                    min={createdFrom ?? undefined}
                                    max={new Date().toISOString().split("T")[0]}
                                    disabled={!createdFrom}
                                    value={createdTo || ""}
                                    onChange={(e) => setCreatedTo(e.target.value)}
                                />
                            </div>

                            <Input
                                label="Longitude"
                                error={currentErrors.locLon}
                                resetError={() =>
                                    setCurrentErrors((prevState) => {
                                        const { locationLong: _, ...rest } = prevState;
                                        return rest;
                                    })
                                }
                                name="locationLong"
                                value={longitude}
                                onChange={(e) => {
                                    setLongitude(e.target.value);
                                }}
                            />
                            <Input
                                label="Latitude"
                                error={currentErrors.locationLatt}
                                resetError={() =>
                                    setCurrentErrors((prevState) => {
                                        const { locationLatt: _, ...rest } = prevState;
                                        return rest;
                                    })
                                }
                                name="locationLatt"
                                value={latitude}
                                onChange={(e) => {
                                    setLatitude(e.target.value);
                                }}
                            />
                </div>
                <div className="five fields">

                  <SearchDropdown
                    key={1}
                    editMode={editMode}
                    reset={searchFieldReset}
                    setReset={setSearchFieldReset}
                    onResultSelect={(data) => {
                      setIsCountrySet(true);
                      console.log("Country selected:", data);
                      setCountry(data);
                    }}
                    api={countryStartsWith}
                    fieldInfo={{
                      tableName: "masterdata.tms_m_reg_country",
                      primaryKey: "cou_id",
                      fieldName: "cou_country",
                      id: country,
                    }}
                    label="Country"
                    error={currentErrors.country}
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { country: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                  />
                  <SearchDropdown
                    key={1}
                    editMode={editMode}
                    reset={searchFieldReset}
                    setReset={setSearchFieldReset}
                    onResultSelect={(data) => {
                      setIsStateSet(true);
                      setState(data);
                    }}
                    api={stateByCountryAndStartsWith}
                    apiDependency={{ id: country }}
                    fieldInfo={{
                      tableName: "masterdata.tms_m_reg_state",
                      primaryKey: "stt_id",
                      fieldName: "stt_state_name",
                      id: state,
                    }}
                    label="State"
                    error={currentErrors.state}
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { state: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    disabled={!isCountrySet}
                  />

                            <SearchDropdown
                                key={`district-${state}-${resetKey}`}
                                editMode={editMode}
                                onResultSelect={(data) => setDistrict(data)}
                                api={districtByStateAndStartsWith}
                                apiDependency={{ id: state }}
                                fieldInfo={{
                                    tableName: "masterdata.tms_m_reg_district",
                                    primaryKey: "dis_id",
                                    fieldName: "dis_district",
                                    id: district,
                                }}
                                label="District"
                                error={currentErrors.district}
                                resetError={() =>
                                    setCurrentErrors((prev) => {
                                        const { district: _, ...rest } = prev;
                                        return rest;
                                    })
                                }
                                disabled={!state}
                            />

                            <div className="field">
                                <label>Status</label>
                                <Select
                                    style={{ height: "20px" }}
                                    options={statusOpt}
                                    value={statusType}
                                    onChange={(e, data) => setStatusType(data.value)}
                                />
                            </div>

                            <div className="field">
                                <label>Active</label>
                                <Select
                                    style={{ height: "20px" }}
                                    options={activeOptions}
                                    value={activeType}
                                    onChange={(e, data) => setActiveType(data.value)}
                                />
                            </div>
                        </div>

                        {/* Filter Action Control Bar */}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: "10px",
                                marginTop: "15px",
                                paddingTop: "15px",
                                borderTop: "1px solid #eeeeee",
                            }}
                        >
                            <Button template="searchReset" onClick={handleReset} />
                            <Button template="search" onClick={handleSearch} />
                        </div>
                    </form>
                )}
            </div>

            {/* Results Grid Display Area */}
            {searchClicked && (
                <div style={{ marginTop: "10px" }}>
                    {searchSubmitLoading ? (
                        <LoadingSpinner />
                    ) : (
                        <Grid
                            heading="Locations Record"
                            content={searchRes}
                            showDownloadButton={true}
                            downloadParam=""
                            downloadAction="Location Excel Download"
                            reportName="location_master.csv"
                            downloadOnlySelected={false}
                            isLoading={isResultLoading}
                            config={[
                                {
                                    headerName: "Location Code",
                                    pinned: "left",
                                    field: "locationCode",
                                },
                                {
                                    headerName: "Location Name",
                                    field: "locaName",
                                    pinned: "left",
                                    cellRenderer: ResizableScrollingTextRenderer,
                                },
                                {
                                    headerName: "Type",
                                    field: "locaType",
                                },
                                {
                                    headerName: "Created Date",
                                    field: "fromDate",
                                    minWidth: 200,
                                },
                                {
                                    headerName: "Customer",
                                    field: "customer",
                                    cellRenderer: customerCellRenderer,
                                },
                                {
                                    headerName: "Longitude",
                                    field: "longitude",
                                },
                                {
                                    headerName: "Latitude",
                                    field: "latitude",
                                },
                                {
                                    headerName: "Address",
                                    field: "locationAddress",
                                    cellRenderer: ResizableScrollingTextRenderer,
                                },
                                {
                                    headerName: "Country",
                                    field: "country",
                                },
                                {
                                    headerName: "State",
                                    field: "state",
                                },
                                {
                                    headerName: "District",
                                    field: "district",
                                },
                                {
                                    headerName: "Status",
                                    field: "status",
                                    pinned: "right",
                                    cellRenderer: StatusCellStyle,
                                },
                                {
                                    headerName: "Active",
                                    field: "activeStatus",
                                    pinned: "right",
                                    cellRenderer: ActiveCellStyle,
                                },
                            ]}
                            onCellDoubleClicked={handleRowDoubleClick}
                        />
                    )}
                </div>
            )}

      {/* Side Drawer */}
      <SideDrawer
        open={isDrawerOpen}
        handleDrawerClose={handleDrawerClose}
        head={
          editMode ? (
            <div className="drawer-header">
              <div
                style={{ padding: "10px 16px", borderBottom: "1px solid #ccc" }}
              >
                <h3>Location Code: {editLocation?.location?.locCode}</h3>
              </div>
            </div>
          ) : null
        }
        body={
          <div className="drawer-content" style={{ width: "60vw" }}>
            <AddLocation
              currentValues={editLocation}
              setCurrentValues={setEditLocation}
              setEditMode={setEditMode}
              editMode={editMode}
            />
          </div>
        }
        style={{ width: "60vw", boxShadow: "none" }}
      />
    </div>
  );
};

export default LocationCreation;
